// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
// CRUD
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
// Services and Models
import {
  DrugGroup,
  DrugGroupActionTypes,
  DrugGroupOnServerCreated,
  DrugGroupOnServerUpdated,
  selectDrugGroupById,
  selectDrugGroupError,
  selectDrugGroupLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-drug-group-edit',
  templateUrl: './drug-group-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrugGroupEditComponent implements OnInit, OnDestroy {
  // Public properties
  drugGroup: DrugGroup;
  drugGroupId$: Observable<number>;
  companyId: number;
  oldDrugGroup: DrugGroup;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  drugGroupForm: FormGroup;
  hasFormErrors = false;
  isViewMode: boolean;
  clicked: boolean;
  isViewMode$: Observable<boolean>;
  // sticky portlet header margin
  private headerMargin: number;
  // Subscriptions
  private subscriptions: Subscription[] = [];
  params: any;

  /**
   * Component constructor
   *
   * @param store
   * @param activatedRoute
   * @param router
   * @param drugGroupFB
   * @param layoutUtilsService
   * @param translate
   * @param cdr
   */
  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private drugGroupFB: FormBuilder,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    // this line due to make the navigation service return to drug groups list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/drug-groups');
    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
    });
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/drug-groups');
      }
      this.companyId = res !== undefined ? res.companyID : 1;
    });
    const errorSubscription = this.store
      .pipe(select(selectDrugGroupError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
          this.clicked = false;
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectDrugGroupLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case DrugGroupActionTypes.DrugGroupCreated:
              successMsg = this.translate.instant('DRUG_GROUP.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case DrugGroupActionTypes.DrugGroupUpdated:
              successMsg = this.translate.instant('DRUG_GROUP.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
            this.goBack();
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.subscriptions.push(userSub);
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        this.store.pipe(select(selectDrugGroupById(id))).subscribe((result) => {
          if (!result) {
            this.router.navigateByUrl('/lists/drug-groups');
          }
          this.drugGroup = result;
          this.loadDrugGroup();
        });
      } else {
        this.drugGroup = new DrugGroup();
        this.loadDrugGroup();
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(routeSub);
    // sticky portlet header
    window.onload = () => {
      const style = getComputedStyle(document.getElementById('kt_header'));
      this.headerMargin = parseInt(style.height, 0);
    };
  }

  loadDrugGroup() {
    if (!this.drugGroup) {
      this.goBack();
      return;
    }
    this.drugGroupId$ = of(this.drugGroup.id);
    this.oldDrugGroup = Object.assign({}, this.drugGroup);
    this.initDrugGroup();
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init drugGroup
   */
  initDrugGroup() {
    this.createForm();
    this.loadingSubject.next(false);
    if (!this.drugGroup.id) {
      this.isViewMode = false;
    } else this.isViewMode = this.activatedRoute.url[`_value`][1]?.path === 'view';
    this.isViewMode$ = of(this.isViewMode);
  }

  /**
   * Create form
   */
  createForm() {
    this.drugGroupForm = this.drugGroupFB.group({
      name: [this.drugGroup.name, Validators.required],
      copay_assistance: [this.drugGroup.copay_assistance],
      copay_assistance_url: [this.drugGroup.copay_assistance_url],
      note: [this.drugGroup.note],
      is_active: [this.drugGroup.is_active ? this.drugGroup.is_active : false],
    });
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.drugGroupForm.disable();
    }
  }

  
  goBack() {
    this.loadingSubject.next(false);
    this.navigationService.goBack();
  }

  /**
   * Reset
   */
  reset() {
    this.drugGroup = Object.assign({}, this.oldDrugGroup);
    this.createForm();
    this.drugGroupForm.controls[`entity_type`].enable();
    this.hasFormErrors = false;
    this.drugGroupForm.markAsPristine();
    this.drugGroupForm.markAsUntouched();
    this.drugGroupForm.updateValueAndValidity();
  }

  /**
   * Save data
   *
   */
  onSumbit() {
    this.hasFormErrors = false;
    this.clicked = true;
    const controls = this.drugGroupForm.controls;
    /** check form */
    if (this.drugGroupForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }
    // tslint:disable-next-line:prefer-const
    const editedDrugGroup = this.prepareDrugGroup();
    if (editedDrugGroup.id > 0) {
      this.updateDrugGroup(editedDrugGroup);
      return;
    }
    this.addDrugGroup(editedDrugGroup);
  }

  /**
   * Returns object for saving
   */
  prepareDrugGroup(): DrugGroup {
    const controls = this.drugGroupForm.controls;
    const _drugGroup = new DrugGroup();
    _drugGroup.id = this.drugGroup.id;
    _drugGroup.name = controls.name.value;
    _drugGroup.copay_assistance = controls.copay_assistance.value;
    _drugGroup.copay_assistance_url = controls.copay_assistance_url.value;
    _drugGroup.note = controls.note.value;
    _drugGroup.is_active = controls.is_active.value;
    for (const [key, value] of Object.entries(_drugGroup)) {
      if (typeof value === 'string') {
        _drugGroup[key] = value.trim();
      }
    }
    return _drugGroup;
  }

  /**
   * Add drugGroup
   *
   * @param _drugGroup
   */
  addDrugGroup(_drugGroup: DrugGroup) {
    this.loadingSubject.next(true);
    this.store.dispatch(new DrugGroupOnServerCreated({ drugGroup: _drugGroup }));
  }

  /**
   * Update drugGroup
   *
   * @param _drugGroup
   */
  updateDrugGroup(_drugGroup: DrugGroup) {
    this.loadingSubject.next(true);

    const updateDrugGroup: Update<DrugGroup> = {
      id: _drugGroup.id,
      changes: _drugGroup,
    };

    this.store.dispatch(
      new DrugGroupOnServerUpdated({
        partialDrugGroup: updateDrugGroup,
        drugGroup: _drugGroup,
      }),
    );
  }

  /**
   * Checking control validation
   *
   * @param controlName
   * @param validationType
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.drugGroupForm.controls[controlName];
    if (!control) {
      return false;
    }

    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  /**
   * Close alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  /**
   * Returns status string
   */
  getStatusString(): string {
    switch (this.drugGroupForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  editDrugGroup(id) {
    this.navigationService.navigateTo('lists/drug-groups/edit/' + id);
  }

  getTitle() {
    let result = 'New Drug Group';
    if (!this.drugGroup || !this.drugGroup.id) {
      return result;
    }
    result = `Drug Group - ${this.drugGroup.name}`;
    return result;
  }
}
